import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Input, Select, Switch, Spin, Empty } from "antd";
import { SaveOutlined, CloseOutlined, CheckCircleFilled, MinusCircleOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import SuccessOverlay from "../../components/feedback/SuccessOverlay";
import {
  getUsuario, editarUsuario, resetPasswordUsuario,
  getRoles, getPermisosUsuario, putPermisosUsuario,
} from "./usuariosApi";
import { MODULOS, ACTIONS } from "../../constants/modulos";
import { PATH } from "../../redux/utils";
import axios from "axios";
import { PERMISO_SOLICITAR_CAMBIOS, sinPermisosEspeciales } from "../../utils/permisosEspeciales";

import "./EventoDetallePage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

// El backend guarda permisos por 9 modulos del sidebar + "estadisticas"
// (que no tiene entrada propia en el sidebar, pero si controla el acceso al
// dashboard/reportes). Se agrega solo para este editor.
const MODULOS_PERMISOS = [
  ...MODULOS,
  { key: "estadisticas", label: "Estadísticas", icon: <i className="fa-solid fa-chart-line" /> },
];

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const ACTION_COLORS = {
  CREATE: { label: "Creado", color: "#16a34a", bg: "#dcfce7" },
  UPDATE: { label: "Editado", color: "var(--brand-primary)", bg: "var(--brand-primary-soft)" },
  DELETE: { label: "Eliminado", color: "#dc2626", bg: "#fee2e2" },
};
const getActionConfig = (a) =>
  ACTION_COLORS[String(a || "").toUpperCase()] || { label: a || "Evento", color: "#64748b", bg: "#f1f5f9" };

export default function UsuarioDetallePage() {
  const { idUser } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [roles, setRoles] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState({ show: false, title: "", subtitle: "" });

  const [permisos, setPermisos] = useState({});
  const [permisosOriginal, setPermisosOriginal] = useState({});
  const [loadingPerm, setLoadingPerm] = useState(false);
  const [savingPerm, setSavingPerm] = useState(false);
  const [editingPermisos, setEditingPermisos] = useState(false);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchUsuario = useCallback(() => {
    setLoading(true);
    getUsuario(idUser)
      .then((res) => setUsuario(res.data))
      .catch(() => toast.error("No se pudo cargar el usuario"))
      .finally(() => setLoading(false));
  }, [idUser]);

  useEffect(() => { fetchUsuario(); }, [fetchUsuario]);

  useEffect(() => {
    getRoles()
      .then((res) => setRoles(res.data?.items ?? res.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "permisos") return;
    setLoadingPerm(true);
    getPermisosUsuario(idUser)
      .then((res) => {
        setPermisos(res.data || {});
        setPermisosOriginal(res.data || {});
      })
      .catch(() => toast.error("No se pudieron cargar los permisos"))
      .finally(() => setLoadingPerm(false));
  }, [activeTab, idUser]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    const token = localStorage.getItem("token");
    axios.get(`${PATH}/usuarios/${idUser}/actividad`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => setActividad(res.data?.items ?? res.data ?? []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idUser]);

  const esAdminRol = !!usuario && String(usuario.rol || "").toLowerCase() === "administrador";
  const isActive = usuario && (usuario.active === 1 || usuario.active === true);

  const startEdit = () => {
    setEditForm({
      id_rol: usuario.id_rol,
      nombre: usuario.nombre,
      correo: usuario.correo,
      active: isActive,
      nuevaPassword: "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSaving(true);
    try {
      await editarUsuario(idUser, {
        id_rol: editForm.id_rol,
        nombre: editForm.nombre.trim(),
        correo: editForm.correo?.trim(),
        active: !!editForm.active,
      });
      if (editForm.nuevaPassword?.trim()) {
        await resetPasswordUsuario(idUser, { password: editForm.nuevaPassword.trim() });
      }
      setEditing(false);
      fetchUsuario();
      setSavedFeedback({ show: true, title: "¡Cambios guardados!", subtitle: "Los datos del usuario se actualizaron correctamente." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const togglePermiso = (modKey, action, checked) => {
    setPermisos((prev) => {
      const cur = { ...(prev[modKey] || {}) };
      cur[action] = checked;
      if (action === "modulo" && !checked) ACTIONS.forEach((a) => { cur[a.key] = false; });
      if (action === "modulo" && checked) cur.consultar = true;
      if (action !== "modulo" && checked) { cur.modulo = true; cur.consultar = true; }
      return { ...prev, [modKey]: cur };
    });
  };

  const handleSavePermisos = async () => {
    setSavingPerm(true);
    try {
      // El permiso especial viaja aparte: el dict `permisos` del PUT solo admite modulos.
      const res = await putPermisosUsuario(
        idUser, sinPermisosEspeciales(permisos), !!permisos[PERMISO_SOLICITAR_CAMBIOS]
      );
      const saved = res.data?.permisos || res.data || permisos;
      setPermisos(saved);
      setPermisosOriginal(saved);
      setEditingPermisos(false);
      toast.success("Permisos guardados");
      window.dispatchEvent(new Event("permisos-refresh"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron guardar los permisos");
    } finally {
      setSavingPerm(false);
    }
  };

  const handleCancelPermisos = () => {
    setPermisos(permisosOriginal);
    setEditingPermisos(false);
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!usuario) {
    return <div className="cd-main" style={{ padding: 40 }}><Empty description="Usuario no encontrado" /></div>;
  }

  const tabBtn = (key, icon, label) => (
    <button className={`cd-tab-btn ${activeTab === key ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab(key)}>
      <i className={`fa-solid ${icon}`} /> {label}
    </button>
  );

  const field = (label, value) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value || "—"}</div>
    </div>
  );

  const labelStyle = { fontSize: 12, color: "#64748b" };

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(usuario.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-at" /> {usuario.correo}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user-shield" /> {usuario.rol || "Sin rol"}</span>
                <span className="cd-meta-item">
                  <i className="fa-solid fa-circle" style={{ color: isActive ? "#16a34a" : "#dc2626" }} /> {isActive ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              <Button onClick={() => navigate("/app/usuarios")}>Volver</Button>
            </div>
          </div>
          <div className="cd-header-tabs">
            {tabBtn("datos", "fa-file-lines", "Información")}
            {tabBtn("permisos", "fa-shield-halved", "Permisos")}
            {tabBtn("actividad", "fa-clock", "Actividad")}
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-user"></i></div>
              <h2 className="cd-card-title">Datos de la cuenta</h2>
              {!editing ? (
perm("usuarios", "editar") && (
                  <Button className="cd-btn-edit" onClick={startEdit} style={{ marginLeft: "auto" }}>Editar</Button>
                )
              ) : (
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <Button size="small" icon={<CloseOutlined />} onClick={() => setEditing(false)} disabled={saving}>Cancelar</Button>
                  <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}>Guardar</Button>
                </div>
              )}
            </div>
            {!editing ? (
              <div>
                {field("Nombre", usuario.nombre)}
                {field("Correo", usuario.correo)}
                {field("Rol", usuario.rol)}
                {field("Creado", fmtDatetime(usuario.datetime || usuario.created_at))}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12, maxWidth: 480 }}>
                <div>
                  <div style={labelStyle}>Nombre</div>
                  <Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
                </div>
                <div>
                  <div style={labelStyle}>Correo</div>
                  <Input value={editForm.correo} onChange={(e) => setEditForm({ ...editForm, correo: e.target.value })} />
                </div>
                <div>
                  <div style={labelStyle}>Rol</div>
                  <Select style={{ width: "100%" }} value={editForm.id_rol} onChange={(v) => setEditForm({ ...editForm, id_rol: v })}
                    options={roles.map((r) => ({ value: r.id_rol ?? r.id, label: r.nombre }))} />
                </div>
                <div>
                  <div style={labelStyle}>Nueva contraseña (dejar vacío para no cambiarla)</div>
                  <Input.Password value={editForm.nuevaPassword} autoComplete="new-password" onChange={(e) => setEditForm({ ...editForm, nuevaPassword: e.target.value })} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch checked={!!editForm.active} onChange={(v) => setEditForm({ ...editForm, active: v })} /> Cuenta activa
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "permisos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-shield-halved"></i></div>
              <div>
                <h2 className="cd-card-title" style={{ marginBottom: 2 }}>Permisos del usuario</h2>
                <div style={{ fontSize: 12, color: "#64748b" }}>Define qué acciones puede realizar este usuario en cada módulo del sistema.</div>
              </div>
              {!editingPermisos ? (
perm("usuarios", "editar") && !esAdminRol && (
                  <Button type="primary" onClick={() => setEditingPermisos(true)}
                  style={{ marginLeft: "auto", backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}>Editar permisos</Button>
                )
              ) : (
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <Button size="small" icon={<CloseOutlined />} onClick={handleCancelPermisos} disabled={savingPerm}>Cancelar</Button>
                  <Button size="small" type="primary" icon={<SaveOutlined />} loading={savingPerm} onClick={handleSavePermisos}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}>Guardar permisos</Button>
                </div>
              )}
            </div>
            {esAdminRol && (
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                Este usuario es Administrador: tiene acceso total a todos los módulos y no se le pueden restringir permisos.
              </div>
            )}
            {loadingPerm ? <Spin /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "10px 6px", fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>Módulo</th>
                      {ACTIONS.map((a) => (
                        <th key={a.key} style={{ padding: "10px 6px", textAlign: "center", fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>
                          {a.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULOS_PERMISOS.map((m) => (
                      <tr key={m.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "10px 6px", fontWeight: 600 }}>{m.icon} <span style={{ marginLeft: 8 }}>{m.label}</span></td>
                        {ACTIONS.map((a) => (
                          <td key={a.key} style={{ padding: "10px 6px", textAlign: "center" }}>
                            {editingPermisos && !esAdminRol ? (
                              <Switch
                                size="small"
                                checked={!!permisos[m.key]?.[a.key]}
                                onChange={(checked) => togglePermiso(m.key, a.key, checked)}
                                style={permisos[m.key]?.[a.key] ? { backgroundColor: "#16a34a" } : undefined}
                              />
                            ) : (esAdminRol || permisos[m.key]?.[a.key]) ? (
                              <CheckCircleFilled style={{ color: "#16a34a", fontSize: 18 }} />
                            ) : (
                              <MinusCircleOutlined style={{ color: "#cbd5e1", fontSize: 18 }} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "permisos" && (
          <div className="cd-card" style={{ marginTop: 20 }}>
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-star"></i></div>
              <div>
                <h2 className="cd-card-title" style={{ marginBottom: 2 }}>Permisos especiales</h2>
                <div style={{ fontSize: 12, color: "#64748b" }}>Permisos que no dependen de un módulo en particular.</div>
              </div>
            </div>
            {esAdminRol && (
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                Este usuario es Administrador: tiene acceso total a todos los módulos y no se le pueden restringir permisos.
              </div>
            )}
            {loadingPerm ? <Spin /> : (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
                <Switch
                  checked={esAdminRol || !!permisos[PERMISO_SOLICITAR_CAMBIOS]}
                  disabled={esAdminRol || !editingPermisos}
                  onChange={(checked) => setPermisos((prev) => ({ ...prev, [PERMISO_SOLICITAR_CAMBIOS]: checked }))}
                  style={(esAdminRol || permisos[PERMISO_SOLICITAR_CAMBIOS]) ? { backgroundColor: "#16a34a" } : undefined}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>Puede solicitar cambios al sistema</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Permite enviar solicitudes de cambio o reportar errores directamente al proveedor del software.
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : actividad.length === 0 ? (
              <Empty description="Sin actividad registrada para este usuario" />
            ) : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || item.id || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message || item.descripcion}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime || item.fecha)}</span>
                          {item.user_name && (
                            <div className="cd-actividad-user">
                              <span>{item.user_name.charAt(0).toUpperCase() + item.user_name.slice(1)}</span>
                              {item.user_email && <span className="cd-actividad-email">{item.user_email}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <SuccessOverlay
        show={savedFeedback.show}
        title={savedFeedback.title}
        subtitle={savedFeedback.subtitle}
        onDone={() => setSavedFeedback({ show: false, title: "", subtitle: "" })}
      />
    </div>
  );
}
